"use client";

import { useRef } from "react";
import Image from "next/image";
import { motion, useMotionTemplate, useScroll, useTransform } from "framer-motion";
import type { HomeComplexityClaritySectionContent } from "@/features/home/types/home-page-content";
interface HomeComplexityClaritySectionProps {
  content: HomeComplexityClaritySectionContent;
}

/**
 * Scroll-driven "complexity -> clarity" reveal.
 *
 * The section is 300vh tall with a sticky inner stage, so the whole sequence
 * plays out as the user scrolls through it:
 *  1. Frame 1 ("complexity"): the image-filled headline zooms in as if pulling
 *     the viewer closer, then blurs and fades away.
 *  2. Frame 2 ("clarity"): the full image sharpens out of a blur, the
 *     "to clarity" line settles in, and the composition HOLDS clear for a
 *     stretch of scroll before gently lifting away.
 */
export function HomeComplexityClaritySection({
  content
}: HomeComplexityClaritySectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"]
  });

  // NOTE: every motion value below is a 2-keyframe useTransform. Multi-keyframe
  // opacity transforms (e.g. [0, 1, 1, 0]) did not update under Turbopack here,
  // so any "hold" is composed by multiplying a fade-in and a fade-out value.

  // --- Frame 1: complexity zooms in, then dissolves ---
  const complexityScale = useTransform(scrollYProgress, [0, 0.4], [1, 1.6]);
  // Derived (passthrough) rather than a direct scrollYProgress transform: a
  // direct opacity value sitting next to the `filter` value on this same div
  // did not update under Turbopack, while a derived one does.
  const complexityFade = useTransform(scrollYProgress, [0.3, 0.42], [1, 0]);
  const complexityOpacity = useTransform([complexityFade], ([fade]: number[]) => fade);
  const complexityBlurAmount = useTransform(scrollYProgress, [0.26, 0.4], [0, 14]);
  const complexityBlur = useMotionTemplate`blur(${complexityBlurAmount}px)`;

  // --- Frame 2: clarity sharpens in, holds, then lifts away ---
  const clarityBlurAmount = useTransform(scrollYProgress, [0.34, 0.5], [16, 0]);
  const clarityBlur = useMotionTemplate`blur(${clarityBlurAmount}px)`;
  // Fades in by ~0.46 and then STAYS (no exit animation). Once the frame is
  // fully revealed it holds until the section unpins and the next section
  // scrolls straight in — so there is no white flash on the way out.
  const clarityFadeIn = useTransform(scrollYProgress, [0.34, 0.46], [0, 1]);
  const clarityOpacity = useTransform([clarityFadeIn], ([fadeIn]: number[]) => fadeIn);

  // The resolving "to clarity" line arrives last for a settled feeling.
  // Opacity is derived (see note above) so it animates alongside its y transform.
  const clarityLineFade = useTransform(scrollYProgress, [0.5, 0.64], [0, 1]);
  const clarityLineOpacity = useTransform([clarityLineFade], ([fade_0]: number[]) => fade_0);
  const clarityLineY = useTransform(scrollYProgress, [0.5, 0.64], [28, 0]);
  const imageBackgroundStyle = {
    backgroundImage: `url(${content.backgroundImageUrl})`,
    backgroundSize: "cover",
    backgroundPosition: "center"
  } as const;
  return <section ref={sectionRef} id={content.id} className="relative z-[40] h-[200vh] w-full bg-white">
      <div className="sticky top-0 flex h-[100svh] w-full items-center justify-center overflow-hidden">
        {/* Frame 1 — complexity: image-filled headline zooming in, then dissolving */}
        <motion.div style={{
        opacity: complexityOpacity,
        filter: complexityBlur
      }} className="absolute inset-0 z-10 flex items-center justify-center bg-white px-6">
          <motion.h2 style={{
          ...imageBackgroundStyle,
          scale: complexityScale
        }} className="bg-clip-text text-center font-serif text-5xl font-medium tracking-tight text-transparent sm:text-7xl lg:text-8xl">
            {content.complexityText}
          </motion.h2>
        </motion.div>

        {/* Frame 2 — clarity: full image sharpening with the resolved headline */}
        <motion.div style={{
        opacity: clarityOpacity
      }} className="absolute inset-0 z-20">
          <motion.div style={{
          filter: clarityBlur
        }} className="absolute inset-0">
            <Image src={content.backgroundImageUrl} alt={content.backgroundImageAlt} fill sizes="100vw" className="object-cover object-center" />
            <div className="absolute inset-0 bg-black/35" />
          </motion.div>

          <div className="relative flex h-full w-full items-center justify-center px-6">
            <h2 className="text-center font-serif text-4xl leading-tight font-medium tracking-tight text-white sm:text-6xl lg:text-7xl">
              {content.complexityText}
              <motion.span style={{
              opacity: clarityLineOpacity,
              y: clarityLineY
            }} className="mt-2 block">
                {content.clarityLead}{" "}
                <em className="font-light italic">{content.clarityEmphasis}</em>
              </motion.span>
            </h2>
          </div>
        </motion.div>
      </div>
    </section>;
}